import styled from "styled-components";

import { type ColumnsType, Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
// Deliberately reuses the Demand Analysis metric card so the cards stay visually identical.
// This crosses app-shared -> market-research; suppressed rather than duplicating the card.
// fallow-ignore-next-line boundary-violation
import { MetricCard } from "app/pages/market-research/features/search-trends-metric-cards/shared/MetricCard";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import type { ITopMover, TopMoverBucket } from "../websiteRankings.types";

import { RankChangeValue } from "./RankChangeValue";

const DISPLAY_LIMIT = 5;
const RANK_COLUMN_WIDTH = 32;
const RANK_VALUE_COLUMN_WIDTH = 88;
const TABLE_BORDERS = { header: false, rowBorders: true, lastRowBorder: true };

const Favicon = styled.img`
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    border-radius: 3px;
`;

export type SectionVariant = TopMoverBucket;

interface ISectionConfig {
    titleKey: string;
    titleDefault: string;
    tooltipKey: string;
    tooltipDefault: string;
    metricKey: string;
    metricDefault: string;
}

const SECTION_CONFIG: Record<SectionVariant, ISectionConfig> = {
    climbers: {
        titleKey: "website.rankings.top.movers.climbers",
        titleDefault: "Top climbers",
        tooltipKey: "website.rankings.top.movers.climbers.tooltip",
        tooltipDefault: "Domains that gained the most rank positions vs the prior comparable period.",
        metricKey: "website.rankings.top.movers.column.rank.change",
        metricDefault: "Rank change",
    },
    fallers: {
        titleKey: "website.rankings.top.movers.fallers",
        titleDefault: "Top fallers",
        tooltipKey: "website.rankings.top.movers.fallers.tooltip",
        tooltipDefault: "Domains that lost the most rank positions vs the prior comparable period.",
        metricKey: "website.rankings.top.movers.column.rank.change",
        metricDefault: "Rank change",
    },
    newEntrants: {
        titleKey: "website.rankings.top.movers.new.entrants",
        titleDefault: "New entrants",
        tooltipKey: "website.rankings.top.movers.new.entrants.tooltip",
        tooltipDefault: "Domains newly present in the ranked set this period.",
        metricKey: "website.rankings.top.movers.column.share",
        metricDefault: "Share",
    },
};

// Matches the main table's TrafficShare cell: below this threshold, a share rounds to a
// misleading "0.00%" — show "< 0.01%" instead.
const MIN_SHARE_TO_DISPLAY = 0.0001;

export const formatShare = (share: number): string =>
    share > 0 && share < MIN_SHARE_TO_DISPLAY ? "< 0.01%" : `${(share * 100).toFixed(2)}%`;

const renderMetric = (variant: SectionVariant, mover: ITopMover) => {
    if (variant === "newEntrants") {
        return (
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon["400"]}
            >
                {formatShare(mover.share)}
            </Text>
        );
    }
    return <RankChangeValue value={mover.rankChange ?? 0} />;
};

const buildColumns = (variant: SectionVariant, config: ISectionConfig): ColumnsType<ITopMover> => [
    {
        key: "rank",
        title: "",
        width: RANK_COLUMN_WIDTH,
        render: (_value, _row, index) => (
            <Text
                fontVariant="caption"
                color={colorsPalettes.carbon["300"]}
            >
                {index + 1}
            </Text>
        ),
    },
    {
        dataIndex: "domain",
        title: translate("website.rankings.top.movers.column.domain", {}, "Domain"),
        ellipsis: true,
        render: (domain: string, row: ITopMover) => (
            <Flex
                alignItems="center"
                gap="8px"
                minWidth="0"
            >
                <Favicon
                    src={getFaviconUrl(domain)}
                    alt=""
                    loading="lazy"
                />
                <Text
                    as="a"
                    href={row.url}
                    fontVariant="body2"
                    color={colorsPalettes.blue["400"]}
                    textDecoration="none"
                >
                    {domain}
                </Text>
            </Flex>
        ),
    },
    {
        key: "metric",
        title: translate(config.metricKey, {}, config.metricDefault),
        align: "right",
        render: (_value, row: ITopMover) => (
            <Flex
                alignItems="center"
                justifyContent="flex-end"
            >
                {renderMetric(variant, row)}
            </Flex>
        ),
    },
    {
        key: "currentRank",
        title: translate("website.rankings.top.movers.column.rank", {}, "Rank"),
        align: "right",
        width: RANK_VALUE_COLUMN_WIDTH,
        render: (_value, row: ITopMover) => (
            <Flex
                alignItems="center"
                justifyContent="flex-end"
                whiteSpace="nowrap"
            >
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon["400"]}
                >
                    {`#${row.rank.toLocaleString()}`}
                </Text>
            </Flex>
        ),
    },
];

interface IMoversCardProps {
    variant: SectionVariant;
    movers: ITopMover[];
    isLoading: boolean;
    isError: boolean;
}

export const MoversCard = ({ variant, movers, isLoading, isError }: IMoversCardProps) => {
    const config = SECTION_CONFIG[variant];
    const visibleMovers = movers.slice(0, DISPLAY_LIMIT);

    return (
        <MetricCard
            title={translate(config.titleKey, {}, config.titleDefault)}
            infoText={translate(config.tooltipKey, {}, config.tooltipDefault)}
            dataAutomation={`website-rankings-top-movers-${variant}`}
            isEmpty={isError || (!isLoading && movers.length === 0)}
            height="auto"
            emptyStateTitleKey="website.rankings.top.movers.no.data.title"
            emptyStateSubtitleKey="website.rankings.top.movers.no.data.subtitle"
        >
            {/* Clip the table to the Card's bottom radius so a full 5-row card keeps rounded
                bottom corners (the Card itself has no overflow:hidden). 6px matches the Card radius. */}
            <Stack
                flex="1"
                overflow="hidden"
                borderRadius="0 0 6px 6px"
            >
                <Table
                    rowKey="domain"
                    theme="white"
                    columnConfig={buildColumns(variant, config)}
                    data={isLoading ? [] : visibleMovers}
                    isLoading={isLoading}
                    loaderRowCount={DISPLAY_LIMIT}
                    bordersConfig={TABLE_BORDERS}
                    sticky={false}
                    pagination={{ pageSize: 100, hideOnSinglePage: true }}
                />
            </Stack>
        </MetricCard>
    );
};

MoversCard.displayName = "MoversCard";
